import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import {
  accountLiabilitiesEndPoints,
  accountServiceAssetsEndPoints,
  accountServiceRequestEndPoints,
  accountStatementEndPoints,
  accountpoints,
  customerAppStatusEndPoints,
  directDebits,
  onboardingEndPoints,
} from "../urlConfig/apiEndpoints";
import type { ApiResponse } from "./ApiResponse";
import type {
  Account,
  BalanceBreakdown,
  BankCurrencyApiResponse,
  Currency,
  DirectDebitAccount,
  DirectDebitAccountPayload,
  DirectDebitMandatesPayload,
  GxAccountBankCurrencyApiResponse,
  GxAccountUpdateFavAccApiPayload,
  GxAccountUpdateFavAccApiResponse,
  GxAppStatusApiPayload,
  GxAppStatusApiResponse,
  GxAssetsCasaApiPayload,
  GxAssetsCasaResponse,
  GxAssetsCasaSummaryApiPayload,
  GxAssetsCasaSummaryApiResponse,
  GxAssetsDepositSummaryApiPayload,
  GxLiabilitiesCardSummaryApiPayload,
  GxLiabilitiesLoanActivitiesApiPayload,
  GxLiabilitiesLoanActivitiesApiResponse,
  GxLiabilitiesLoanApiPayload,
  GxLiabilitiesLoanApiResponse,
  GxLiabilitiesLoanSummaryApiPayload,
  GxLiabilitiesLoanSummaryApiResponse,
  GxStatementGxlookupsApiPayload,
  GxStatementGxlookupsApiResponse,
  GxStatementSummaryApiPayload,
  MandateDetails,
  MandateSummaryResponse,
} from "../../types/accounts";

import {
  extractErrorMessage,
  showError,
  showSuccess,
} from "../errorservices/errorservices";
import { getToken } from "@/app/services/context/tokenGetter";
import type {
  GetAccountSubfeaturePayload,
  GetAccountSubfeatureResponse,
  GetLoanPayNowInfoPayload,
  PaymentType,
} from "@/app/types/payments";
import { executeLogout } from "@/app/utils/loginFlow/logoutHelper";
import { store, type RootState } from "@/app/redux/store";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/app/utils/loginFlow/correlationId";

const getTenantId = (): string | undefined => {
  const state = store.getState() as RootState;
  return state.userData?.data?.tenantId;
};

// ---------------- Auth logout registration ----------------
const handleUnauthorized = (err: any) => {
  executeLogout();
  return Promise.reject(err);
};

// ---------------- Generic API handler ----------------
const handleApiRequest = async <T>(
  method: typeof axios.get | typeof axios.post | typeof axios.delete,
  url: string,
  payload: any = {},
  showSuccessMsg?: boolean,
): Promise<ApiResponse<T>> => {
  const correlationId = generateCorrelationId();
  try {
    const token = getToken();
    const tenantId = getTenantId();

    const headers: Record<string, string | undefined> = {
      Accept: "*/*",
      Authorization: `Bearer ${token}`,
      appId: tenantId,
      [CORRELATION_ID_HEADER]: correlationId,
    };
    if (!(payload instanceof FormData))
      headers["Content-Type"] = "application/json";

    const config: AxiosRequestConfig = { headers };
    const response: AxiosResponse =
      method === axios.get || method === axios.delete
        ? await method(url, { ...config, params: payload })
        : await method(url, payload, config);

    const data: ApiResponse<T> = response.data;

    if (data?.status === 0) {
      if (showSuccessMsg && data.errorMessage) showSuccess(data.errorMessage);
      return data;
    }

    if (data?.status === 1) {
      if (showSuccessMsg && data.errorMessage) showSuccess(data.errorMessage);
      return data;
    }

    if (data?.status === 2) {
      showError(undefined, data.errorMessage || "Request failed.");
      throw new Error(data.errorMessage || "Request failed.");
    }

    throw new Error("Unexpected server response.");
  } catch (err: any) {
    const { code, message } = extractErrorMessage(err);

    if (code === 401) {
      return handleUnauthorized(err);
    }

    showError(code, message);
    return Promise.reject(err);
  }
};

// ---------------- Example API handlers ----------------
export const gxAccountBalanceTotalApi = (payload: Currency) =>
  handleApiRequest<BalanceBreakdown>(
    axios.post,
    accountpoints.gxAccountBalanceTotal,
    payload,
    false,
  );

// ---------------- Account Service - Dashboard/Home Page APIs ----------------
export const gxAccountUpdateFavouriteAccountsApi = (
  payload: GxAccountUpdateFavAccApiPayload,
) => {
  return handleApiRequest<GxAccountUpdateFavAccApiResponse>(
    axios.post,
    accountpoints.gxAccountUpdateFavouriteAccounts,
    payload,
  );
}; // Payload and Response added

export const gxAccountFavouriteAccountsApi = (payload: any) =>
  handleApiRequest<Account>(
    axios.post,
    accountpoints.gxAccountFavouriteAccounts,
    payload,
    false,
  );
export const gxAccountBankCurrencyApi = (payload?: any) =>
  handleApiRequest<GxAccountBankCurrencyApiResponse>(
    axios.get,
    accountpoints.gxAccountBankCurrency,
    payload,
    false,
  );
export const gxAccountHistoryRecentApi = (payload?: any) =>
  handleApiRequest<BankCurrencyApiResponse>(
    axios.get,
    accountpoints.gxAccountHistoryRecent,
    payload,
    false,
  );
export const gxAccountImmediateAttentionApi = (payload?: any) =>
  handleApiRequest<any>(
    axios.get,
    accountpoints.gxAccountImmediateAttention,
    payload,
    false,
  );

// ---------------- Assets APIs ----------------
export const gxAssetsDepositApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsDeposit,
    payload,
    false,
  );
export const gxAssetsDepositBalanceApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsDepositBalance,
    payload,
    false,
  );
export const gxAssetsDepositActivitiesApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsDepositActivities,
    payload,
    false,
  );
export const gxAssetsDepositSummaryApi = (
  payload: GxAssetsDepositSummaryApiPayload,
) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsDepositSummary,
    payload,
    false,
  ); // Payload added

export const gxAssetsDebitAccountViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsDebitAccountView,
    payload,
    false,
  );
export const gxAssetsDebitAccountViewSummaryApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsDebitAccountViewSummary,
    payload,
    false,
  );

export const gxAssetsCasaApi = (payload: GxAssetsCasaApiPayload) =>
  handleApiRequest<GxAssetsCasaResponse>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsCasa,
    payload,
    false,
  ); // Payload and responses added

export const gxAssetsCasaBalanceApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsCasaBalance,
    payload,
    false,
  );

export const gxAssetsCasaActivitiesApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsCasaActivities,
    payload,
    false,
  );

export const gxAssetsCasaActivitiesTrackerApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsCasaActivitiesTracker,
    payload,
    false,
  );

export const gxAssetsCasaSummaryApi = (
  payload: GxAssetsCasaSummaryApiPayload,
) =>
  handleApiRequest<GxAssetsCasaSummaryApiResponse>(
    axios.post,
    accountServiceAssetsEndPoints.gxAssetsCasaSummary,
    payload,
    false,
  ); // Payload added

// ---------------- Liabilities APIs ----------------
export const gxLiabilitiesLoanApi = (payload: GxLiabilitiesLoanApiPayload) =>
  handleApiRequest<GxLiabilitiesLoanApiResponse>(
    axios.post,
    accountLiabilitiesEndPoints.gxLiabilitiesLoan,
    payload,
    false,
  ); // Payload and Response added

export const gxLiabilitiesLoanBalanceApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountLiabilitiesEndPoints.gxLiabilitiesLoanBalance,
    payload,
    false,
  );

export const gxLiabilitiesLoanActivitiesApi = (
  payload: GxLiabilitiesLoanActivitiesApiPayload,
) =>
  handleApiRequest<GxLiabilitiesLoanActivitiesApiResponse>(
    axios.post,
    accountLiabilitiesEndPoints.gxLiabilitiesLoanActivities,
    payload,
    false,
  ); // Payload and Response added

export const gxLiabilitiesLoanSummaryApi = (
  payload: GxLiabilitiesLoanSummaryApiPayload,
) =>
  handleApiRequest<GxLiabilitiesLoanSummaryApiResponse>(
    axios.post,
    accountLiabilitiesEndPoints.gxLiabilitiesLoanSummary,
    payload,
    false,
  ); // Payload and Response added

export const gxLiabilitiesCardApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountLiabilitiesEndPoints.gxLiabilitiesCard,
    payload,
    false,
  );

export const gxLiabilitiesCardSummaryApi = (
  payload: GxLiabilitiesCardSummaryApiPayload,
) =>
  handleApiRequest<any>(
    axios.post,
    accountLiabilitiesEndPoints.gxLiabilitiesCardSummary,
    payload,
    false,
  ); // Payload added

export const gxLiabilitiesCardActivitiesApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountLiabilitiesEndPoints.gxLiabilitiesCardActivities,
    payload,
    false,
  );

// ---------------- Statement APIs ----------------
export const gxStatementGxlookupsApi = (
  payload: GxStatementGxlookupsApiPayload,
) =>
  handleApiRequest<GxStatementGxlookupsApiResponse>(
    axios.post,
    accountStatementEndPoints.gxStatementGxlookups,
    payload,
    false,
  ); // Payload and Response added

export const gxCasaStatementRequestApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountStatementEndPoints.gxCasaStatementRequest,
    payload,
    true,
  );
export const gxStatementSummaryApi = (payload: GxStatementSummaryApiPayload) =>
  handleApiRequest<any>(
    axios.post,
    accountStatementEndPoints.gxStatementSummary,
    payload,
    false,
  ); // Payload added

export const gxStatementSummaryDownloadApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountStatementEndPoints.gxStatementSummaryDownload,
    payload,
    false,
  );

// ---------------- Service Request APIs ----------------
export const gxServiceRequestChequebookTypeApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceRequestEndPoints.gxServiceRequestChequebookType,
    payload,
    false,
  );
export const gxServiceRequestChequebookLeavesApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceRequestEndPoints.gxServiceRequestChequebookLeaves,
    payload,
    false,
  );
export const gxServiceRequestChequebookDeliveryTypeApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceRequestEndPoints.gxServiceRequestChequebookDeliveryType,
    payload,
    false,
  );
export const gxServiceRequestChequebookApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceRequestEndPoints.gxServiceRequestChequebook,
    payload,
    false,
  );
export const gxServiceRequestBalanceConfirmationApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceRequestEndPoints.gxServiceRequestBalanceConfirmation,
    payload,
    false,
  );
export const gxServiceRequestChequeBooksApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceRequestEndPoints.gxServiceRequestChequeBooks,
    payload,
    true,
  );
export const gxServiceRequestBalanceConfirmRequestApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    accountServiceRequestEndPoints.gxServiceRequestBalanceConfirmRequest,
    payload,
    true,
  );

// ---------------- Support APIs ----------------
export const gxAccountsBanklistApi = () =>
  handleApiRequest(
    axios.get,
    accountServiceAssetsEndPoints.gxAccountsBanklist,
    {},
    false,
  );

export const gxAppStatusApi = (payload: GxAppStatusApiPayload) =>
  handleApiRequest<GxAppStatusApiResponse>(
    axios.post,
    customerAppStatusEndPoints.gxAppStatus,
    payload,
    false,
  );

// ----------------------------get loanpay info ---------------------------------------------------------

export const gxGetLoanPayNowInfoApi = (payload: GetLoanPayNowInfoPayload) => {
  return handleApiRequest<PaymentType>(
    axios.post,
    accountLiabilitiesEndPoints.gxGetLoanPayNowInfo,
    payload,
  );
};

export const gxGetAccountsSubfeatures = (
  payload: GetAccountSubfeaturePayload,
) => {
  return handleApiRequest<GetAccountSubfeatureResponse[]>(
    axios.post,
    accountLiabilitiesEndPoints.gxAccountSubfeature,
    payload,
  );
};

// ----------------------------gxloginconsentApi-----------------------------------------------------

export const gxloginconsentApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    onboardingEndPoints.growxloginconsent,
    payload,
  );
};

// ---------------------Direct Debits--------------------------------

export const gxGetDirectDebitAccounts = (
  payload: DirectDebitAccountPayload,
) => {
  return handleApiRequest<DirectDebitAccount[]>(
    axios.post,
    directDebits.gxDirectDebitAccounts,
    payload,
  );
};

export const gxGetDirectDebitMandates = (
  payload: DirectDebitMandatesPayload,
) => {
  return handleApiRequest<MandateSummaryResponse>(
    axios.post,
    directDebits.gxDirectDebitMandates,
    payload,
  );
};

export const gxGetMandateDetails = (payload: { directDebitId: number }) => {
  return handleApiRequest<MandateDetails>(
    axios.post,
    directDebits.gxMandateDetails,
    payload,
  );
};

// --------------------ENd ---------------------------------------------
